<script setup>
import { ref, computed, onMounted } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import Copilot from 'dashboard/components-next/copilot/Copilot.vue';
// SiteUp: aba Follow-ups da IA no painel do Copiloto.
import CopilotFollowUps from 'dashboard/components-next/copilot/CopilotFollowUps.vue';
import CopilotRoutines from 'dashboard/components-next/copilot/CopilotRoutines.vue';
import CopilotAccountConfigAPI from 'dashboard/api/copilot/accountConfig';
import { useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useConfig } from 'dashboard/composables/useConfig';
import { useWindowSize } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import wootConstants from 'dashboard/constants/globals';

defineProps({
  conversationInboxType: {
    type: String,
    default: '',
  },
});

const store = useStore();
const { uiSettings, updateUISettings } = useUISettings();
const { isEnterprise } = useConfig();
const { width: windowWidth } = useWindowSize();

const currentUser = useMapGetter('getCurrentUser');
const assistants = useMapGetter('captainAssistants/getRecords');
const uiFlags = useMapGetter('captainAssistants/getUIFlags');
const inboxAssistant = useMapGetter('getCopilotAssistant');
const currentChat = useMapGetter('getSelectedChat');

const isSmallScreen = computed(
  () => windowWidth.value < wootConstants.SMALL_SCREEN_BREAKPOINT
);

// SiteUp: aba ativa do painel — 'chat' (Copiloto nativo), 'routines' ou 'follow_ups'.
const activeTab = ref('chat');

const selectedCopilotThreadId = ref(null);
const canUseCopilot = ref(false);
const messages = computed(() =>
  store.getters['copilotMessages/getMessagesByThreadId'](
    selectedCopilotThreadId.value
  )
);

const copilotThreads = useMapGetter('copilotThreads/getRecords');

const persistSelectedThreadId = threadId => {
  updateUISettings({ selected_copilot_thread_id: threadId ?? null });
};

// Reidrata o painel ao montar/reabrir: busca as threads do usuário e, se houver,
// seleciona a última (ou a persistida nas UISettings, se ainda existir) e carrega
// suas mensagens do backend. Sem isso, o atendente sempre via o empty state mesmo
// com uma conversa do Copiloto em andamento (o histórico ia pro LLM mas não pra UI).
const hydrateLatestThread = async () => {
  try {
    await store.dispatch('copilotThreads/get');
    const threads = copilotThreads.value;
    if (!threads.length) return;

    const persistedId = uiSettings.value.selected_copilot_thread_id;
    const persisted = threads.find(thread => thread.id === persistedId);
    // getRecords already returns newest-first; fall back to the latest thread.
    const targetThread = persisted || threads[0];

    selectedCopilotThreadId.value = targetThread.id;
    await store.dispatch('copilotMessages/get', targetThread.id);
  } catch {
    // Falha ao reidratar não deve quebrar o painel: o atendente ainda pode
    // iniciar uma nova thread normalmente.
  }
};

const fetchCopilotAccess = async () => {
  try {
    await CopilotAccountConfigAPI.get();
    canUseCopilot.value = true;
  } catch {
    canUseCopilot.value = false;
  }
};

const currentAccountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const selectedAssistantId = ref(null);

const activeAssistant = computed(() => {
  const preferredId = uiSettings.value.preferred_captain_assistant_id;

  // If the user has selected a specific assistant, it takes first preference for Copilot.
  if (preferredId) {
    const preferredAssistant = assistants.value.find(a => a.id === preferredId);
    // Return the preferred assistant if found, otherwise continue to next cases
    if (preferredAssistant) return preferredAssistant;
  }

  // If the above is not available, the assistant connected to the inbox takes preference.
  if (inboxAssistant.value) {
    const inboxMatchedAssistant = assistants.value.find(
      a => a.id === inboxAssistant.value.id
    );
    if (inboxMatchedAssistant) return inboxMatchedAssistant;
  }
  // If neither of the above is available, the first assistant in the account takes preference.
  return assistants.value[0];
});

const closeCopilotPanel = () => {
  if (isSmallScreen.value && uiSettings.value?.is_copilot_panel_open) {
    updateUISettings({
      is_contact_sidebar_open: false,
      is_copilot_panel_open: false,
    });
  }
};

const setAssistant = async assistant => {
  selectedAssistantId.value = assistant.id;
  await updateUISettings({
    preferred_captain_assistant_id: assistant.id,
  });
};

const shouldShowCopilotPanel = computed(() => {
  if (!isEnterprise) {
    return false;
  }
  const isCaptainEnabled = isFeatureEnabledonAccount.value(
    currentAccountId.value,
    FEATURE_FLAGS.CAPTAIN
  );
  const { is_copilot_panel_open: isCopilotPanelOpen } = uiSettings.value;
  return (
    canUseCopilot.value &&
    isCaptainEnabled &&
    isCopilotPanelOpen &&
    !uiFlags.value.fetchingList
  );
});

// Reinicia a conversa do Copiloto. Antes isto só zerava o ref local, deixando a
// thread + mensagens órfãs no banco (acumulando) e nunca usando o endpoint
// /reset que existe no backend. Agora, havendo thread ativa, dispara o /reset
// (esvazia a thread server-side + cria mensagem de confirmação) e resincroniza
// as mensagens; sem thread ativa, apenas limpa o estado local.
const handleReset = async () => {
  const threadId = selectedCopilotThreadId.value;
  if (!threadId) {
    persistSelectedThreadId(null);
    return;
  }

  try {
    await store.dispatch('copilotMessages/create', {
      assistant_id: activeAssistant.value.id,
      conversation_id: currentChat.value?.id,
      threadId,
      message: '/reset',
    });
    await store.dispatch('copilotMessages/get', threadId);
  } catch (error) {
    useAlert(error.message);
  }
};

const sendMessage = async message => {
  try {
    if (selectedCopilotThreadId.value) {
      await store.dispatch('copilotMessages/create', {
        assistant_id: activeAssistant.value.id,
        conversation_id: currentChat.value?.id,
        threadId: selectedCopilotThreadId.value,
        message,
      });
    } else {
      const response = await store.dispatch('copilotThreads/create', {
        assistant_id: activeAssistant.value.id,
        conversation_id: currentChat.value?.id,
        message,
      });
      selectedCopilotThreadId.value = response.id;
      persistSelectedThreadId(response.id);
    }
  } catch (error) {
    useAlert(error.message);
  }
};

const runRoutine = async prompt => {
  activeTab.value = 'chat';
  await sendMessage(prompt);
};

onMounted(() => {
  if (isEnterprise) {
    fetchCopilotAccess();
    store.dispatch('captainAssistants/get');
    hydrateLatestThread();
  }
});
</script>

<template>
  <div
    v-if="shouldShowCopilotPanel"
    v-on-click-outside="() => closeCopilotPanel()"
    class="bg-n-surface-2 h-full overflow-hidden flex-col fixed top-0 ltr:right-0 rtl:left-0 z-40 w-full max-w-sm transition-transform duration-300 ease-in-out md:static md:w-[320px] md:min-w-[320px] ltr:border-l rtl:border-r border-n-weak 2xl:min-w-[360px] 2xl:w-[360px] shadow-lg md:shadow-none"
    :class="[
      {
        'md:flex': shouldShowCopilotPanel,
        'md:hidden': !shouldShowCopilotPanel,
      },
    ]"
  >
    <!-- h-full é essencial no mobile: o root só vira flex via md:flex, então abaixo
         de md o flex-1 é inerte e sem h-full o wrapper colapsaria (height:auto),
         quebrando a cadeia de altura do Copilot (scroll + input sairiam da viewport).
         Este wrapper é sempre flex-col: as abas (SiteUp) ficam no topo e o conteúdo
         ativo ocupa o resto via flex-1/min-h-0 em qualquer breakpoint. -->
    <div class="flex-1 min-h-0 h-full flex flex-col">
      <!-- SiteUp: abas do painel — Copiloto (chat nativo) e Follow-ups da IA -->
      <div
        class="shrink-0 flex items-center gap-1 px-3 pt-2 pb-1.5 border-b border-n-weak"
      >
        <button
          type="button"
          class="rounded-md px-2.5 py-1 text-xs font-medium transition-colors"
          :class="
            activeTab === 'chat'
              ? 'bg-n-teal-9/15 text-n-teal-11'
              : 'text-n-slate-11 hover:bg-n-alpha-2'
          "
          @click="activeTab = 'chat'"
        >
          {{ $t('CAPTAIN.COPILOT.TITLE') }}
        </button>
        <button
          type="button"
          class="rounded-md px-2.5 py-1 text-xs font-medium transition-colors"
          :class="
            activeTab === 'routines'
              ? 'bg-n-teal-9/15 text-n-teal-11'
              : 'text-n-slate-11 hover:bg-n-alpha-2'
          "
          @click="activeTab = 'routines'"
        >
          {{ $t('CAPTAIN.COPILOT.ROUTINES.TAB') }}
        </button>
        <button
          type="button"
          class="rounded-md px-2.5 py-1 text-xs font-medium transition-colors"
          :class="
            activeTab === 'follow_ups'
              ? 'bg-n-teal-9/15 text-n-teal-11'
              : 'text-n-slate-11 hover:bg-n-alpha-2'
          "
          @click="activeTab = 'follow_ups'"
        >
          {{ $t('CAPTAIN.COPILOT.FOLLOW_UPS_TAB') }}
        </button>
      </div>
      <!-- v-show preserva o estado/scroll do chat ao alternar de aba -->
      <div v-show="activeTab === 'chat'" class="flex-1 min-h-0">
        <Copilot
          :messages="messages"
          :support-agent="currentUser"
          :conversation-inbox-type="conversationInboxType"
          :assistants="assistants"
          :active-assistant="activeAssistant"
          @set-assistant="setAssistant"
          @send-message="sendMessage"
          @reset="handleReset"
        />
      </div>
      <!-- v-if: monta só ao abrir a aba (o fetch roda no onMounted) -->
      <CopilotFollowUps
        v-if="activeTab === 'follow_ups'"
        class="flex-1 min-h-0"
      />
      <CopilotRoutines
        v-if="activeTab === 'routines'"
        class="flex-1 min-h-0"
        @close="closeCopilotPanel"
        @run-routine="runRoutine"
      />
    </div>
  </div>
  <template v-else />
</template>
